import { Injectable, signal } from '@angular/core';
import { PlanningRow } from './campaign-planning.component';

interface CachedPlanning {
  rows: PlanningRow[];
  locationNames: Map<number, string>;
}

/**
 * Keeps the transformed planning rows of page-hosted grids (campaign model
 * detail) so switching tabs renders instantly instead of re-fetching. Dialog
 * hosts bypass it on purpose: a dialog opens on data that other flows mutate,
 * so it must always fetch fresh.
 */
@Injectable({ providedIn: 'root' })
export class CampaignPlanningCacheService {
  private readonly cache = new Map<string, CachedPlanning>();

  get(campaignId: number, mode: string): CachedPlanning | undefined {
    return this.cache.get(`${campaignId}:${mode}`);
  }

  set(campaignId: number, mode: string, rows: PlanningRow[], locationNames: Map<number, string>): void {
    this.cache.set(`${campaignId}:${mode}`, { rows, locationNames });
  }

  // A dialog host mutates the snapshot without touching this cache; the page host must
  // invalidate before refetching so it does not replay stale rows.
  invalidate(campaignId: number, mode: string): void {
    this.cache.delete(`${campaignId}:${mode}`);
  }

  // Bumped by the host that plans/unplans (the dialog). A page host showing the same
  // campaign+mode watches this tick to refresh live behind the dialog, mirroring how the
  // real-campaign tabs refresh their parent on each change.
  private readonly mutationTicks = signal<Map<string, number>>(new Map());

  markMutated(campaignId: number, mode: string): void {
    const key = `${campaignId}:${mode}`;
    this.mutationTicks.update(ticks => new Map(ticks).set(key, (ticks.get(key) ?? 0) + 1));
  }

  mutationTick(campaignId: number, mode: string): number {
    return this.mutationTicks().get(`${campaignId}:${mode}`) ?? 0;
  }
}
